import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { paymentsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  CreditCard, Zap, Crown, CheckCircle, Loader2, 
  Sparkles, Gift, Clock, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';

const Billing = () => {
  const { user, refreshUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [yearlyBilling, setYearlyBilling] = useState(false);
  const [transactions, setTransactions] = useState([]);

  // Check for payment return
  const sessionId = searchParams.get('session_id');
  const status = searchParams.get('status');

  const getPrice = (monthly) => {
    if (yearlyBilling) {
      return Math.round(monthly * 10 / 12);
    }
    return monthly;
  };

  const pollPaymentStatus = useCallback(async (sid, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.error('Payment status check timed out. Please refresh the page.');
      setCheckingPayment(false);
      return;
    }

    try {
      const response = await paymentsAPI.getStatus(sid);
      const data = response.data;

      if (data.payment_status === 'paid') {
        toast.success('Payment successful! Your account has been upgraded.');
        await refreshUser();
        setSearchParams({});
        setCheckingPayment(false);
        fetchTransactions();
        return;
      } else if (data.status === 'expired') {
        toast.error('Payment session expired. Please try again.');
        setSearchParams({});
        setCheckingPayment(false);
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      if (attempts < maxAttempts) {
        setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
      } else {
        toast.error('Error checking payment status. Please refresh the page.');
        setCheckingPayment(false);
      }
    }
  }, [refreshUser, setSearchParams]);

  useEffect(() => {
    if (sessionId && status === 'success') {
      setCheckingPayment(true);
      toast.info('Verifying your payment...');
      pollPaymentStatus(sessionId);
    } else if (status === 'cancelled') {
      toast.info('Payment was cancelled.');
      setSearchParams({});
    }
  }, [sessionId, status, pollPaymentStatus, setSearchParams]);

  const fetchTransactions = async () => {
    try {
      const response = await paymentsAPI.getHistory();
      setTransactions(response.data.transactions || []);
    } catch (error) {
      console.error('Failed to fetch transactions');
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, []);

  const handleCheckout = async (packageId) => {
    setLoading(true);
    try {
      const response = await paymentsAPI.createCheckout(packageId);
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setLoading(false);
    }
  };

  const isPro = user?.plan === 'pro' || user?.plan === 'power';
  const isPower = user?.plan === 'power';

  if (checkingPayment) {
    return (
      <div className="space-y-6" data-testid="billing-page">
        <div className="flex flex-col items-center justify-center min-h-[400px]">
          <Loader2 className="w-12 h-12 text-neon-mint animate-spin mb-4" />
          <h2 className="font-heading font-bold text-2xl text-white mb-2">Processing Payment</h2>
          <p className="text-gray-400">Please wait while we verify your payment...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="billing-page">
      {/* Header */}
      <div>
        <h1 className="font-heading font-bold text-2xl text-white flex items-center gap-2">
          <CreditCard className="w-6 h-6 text-neon-mint" />
          Billing & Subscription
        </h1>
        <p className="text-gray-400">Manage your subscription and purchase credits</p>
      </div>

      {/* Current Plan Banner */}
      <Card className="bg-gradient-to-r from-neon-mint/10 to-neon-violet/10 border-neon-mint/30">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                {user?.plan === 'power' ? (
                  <Crown className="w-6 h-6 text-neon-violet" />
                ) : user?.plan === 'pro' ? (
                  <Sparkles className="w-6 h-6 text-neon-mint" />
                ) : (
                  <Zap className="w-6 h-6 text-gray-400" />
                )}
                <span className="font-heading font-bold text-xl text-white capitalize">
                  {user?.plan || 'Free'} Plan
                </span>
              </div>
              <p className="text-gray-400">
                {isPower 
                  ? "You have access to all features including AI image generation" 
                  : isPro 
                    ? "You have access to AI copy generation and optimization"
                    : "Upgrade to unlock AI features and more"}
              </p>
            </div>
            <div className="text-right">
              <p className="text-gray-500 text-sm">Credits Balance</p>
              <p className="font-mono font-bold text-3xl text-neon-mint">{user?.credits || 0}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="plans" className="space-y-6">
        <TabsList className="bg-void-paper border border-white/10">
          <TabsTrigger value="plans" className="data-[state=active]:bg-neon-mint/20 data-[state=active]:text-neon-mint">
            <Crown className="w-4 h-4 mr-2" />
            Plans
          </TabsTrigger>
          <TabsTrigger value="credits" className="data-[state=active]:bg-neon-mint/20 data-[state=active]:text-neon-mint">
            <Zap className="w-4 h-4 mr-2" />
            Buy Credits
          </TabsTrigger>
          <TabsTrigger value="history" className="data-[state=active]:bg-neon-mint/20 data-[state=active]:text-neon-mint">
            <Clock className="w-4 h-4 mr-2" />
            History
          </TabsTrigger>
        </TabsList>

        {/* Plans Tab */}
        <TabsContent value="plans">
          {/* Billing Toggle */}
          <div className="flex justify-center mb-8">
            <div className="inline-flex items-center gap-4 p-2 bg-void-paper rounded-full border border-white/10">
              <span className={`text-sm ${!yearlyBilling ? 'text-white' : 'text-gray-500'}`}>Monthly</span>
              <Switch
                checked={yearlyBilling}
                onCheckedChange={setYearlyBilling}
                className="data-[state=checked]:bg-neon-mint"
              />
              <span className={`text-sm ${yearlyBilling ? 'text-white' : 'text-gray-500'}`}>
                Yearly
                <span className="ml-2 px-2 py-0.5 bg-neon-mint/20 text-neon-mint text-xs rounded-full">
                  2 months free
                </span>
              </span>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {/* Free Plan */}
            <Card className={`bg-void-paper border-white/10 ${user?.plan === 'free' ? 'ring-2 ring-gray-500' : ''}`}>
              <CardHeader>
                <CardTitle className="text-white font-heading flex items-center justify-between">
                  <span>Free</span>
                  {user?.plan === 'free' && (
                    <span className="text-xs bg-gray-600 text-white px-2 py-1 rounded-full">Current</span>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <span className="font-heading font-extrabold text-4xl text-white">$0</span>
                  <span className="text-gray-500">/month</span>
                </div>
                <ul className="space-y-3">
                  {['50 starting credits', '1 active promotion', '1 capture page', 'Earn credits by viewing', 'Basic analytics', 'Daily login bonus'].map((f, i) => (
                    <li key={i} className="flex items-center gap-2 text-gray-400 text-sm">
                      <CheckCircle className="w-4 h-4 text-gray-500" />
                      {f}
                    </li>
                  ))}
                </ul>
                <Button variant="outline" disabled className="w-full border-white/20 text-gray-400">
                  {user?.plan === 'free' ? 'Current Plan' : 'Downgrade'}
                </Button>
              </CardContent>
            </Card>

            {/* Pro Plan */}
            <Card className={`bg-void-paper border-2 ${user?.plan === 'pro' ? 'border-neon-mint ring-2 ring-neon-mint' : 'border-neon-mint'} shadow-neon relative`}>
              {user?.plan !== 'pro' && user?.plan !== 'power' && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-neon-mint text-black text-xs font-bold rounded-full">
                  POPULAR
                </div>
              )}
              <CardHeader>
                <CardTitle className="text-white font-heading flex items-center justify-between">
                  <span>Pro</span>
                  {user?.plan === 'pro' && (
                    <span className="text-xs bg-neon-mint text-black px-2 py-1 rounded-full">Current</span>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <span className="font-heading font-extrabold text-4xl text-white">${getPrice(19)}</span>
                  <span className="text-gray-500">/{yearlyBilling ? 'mo' : 'month'}</span>
                  {yearlyBilling && (
                    <p className="text-neon-mint text-sm mt-1">Billed ${getPrice(19) * 12}/year</p>
                  )}
                </div>
                <p className="text-neon-mint text-xs font-medium">Everything in Free, plus:</p>
                <ul className="space-y-3">
                  {['200 bonus credits on upgrade', '100 credits every month', '5 active promotions', '5 capture pages', 'Add leads manually (50/mo)', 'AI content generator', 'Scheduled messages', 'Email your leads'].map((f, i) => (
                    <li key={i} className="flex items-center gap-2 text-gray-300 text-sm">
                      <CheckCircle className="w-4 h-4 text-neon-mint" />
                      {f}
                    </li>
                  ))}
                </ul>
                <Button 
                  onClick={() => handleCheckout(yearlyBilling ? 'pro_yearly' : 'pro_monthly')}
                  disabled={loading || user?.plan === 'pro' || user?.plan === 'power'}
                  className="w-full bg-neon-mint text-black hover:bg-neon-mint/90 font-bold shadow-neon"
                  data-testid="upgrade-pro-btn"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 
                   user?.plan === 'pro' ? 'Current Plan' : 
                   user?.plan === 'power' ? 'Downgrade' : 'Upgrade to Pro'}
                </Button>
              </CardContent>
            </Card>

            {/* Power Plan */}
            <Card className={`bg-void-paper ${user?.plan === 'power' ? 'border-2 border-neon-violet ring-2 ring-neon-violet' : 'border border-neon-violet/50'}`}>
              <CardHeader>
                <CardTitle className="text-white font-heading flex items-center justify-between">
                  <span>Power</span>
                  {user?.plan === 'power' && (
                    <span className="text-xs bg-neon-violet text-white px-2 py-1 rounded-full">Current</span>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <span className="font-heading font-extrabold text-4xl text-white">${getPrice(39)}</span>
                  <span className="text-gray-500">/{yearlyBilling ? 'mo' : 'month'}</span>
                  {yearlyBilling && (
                    <p className="text-neon-violet text-sm mt-1">Billed ${getPrice(39) * 12}/year</p>
                  )}
                </div>
                <p className="text-neon-violet text-xs font-medium">Everything in Pro, plus:</p>
                <ul className="space-y-3">
                  {['500 bonus credits on upgrade', '300 credits every month', 'Unlimited promotions', '10 capture pages', 'Unlimited manual lead add', 'CSV lead import (5,000/upload)', 'AI image generator', 'Scheduled messages', 'Advanced analytics'].map((f, i) => (
                    <li key={i} className="flex items-center gap-2 text-gray-300 text-sm">
                      <CheckCircle className="w-4 h-4 text-neon-violet" />
                      {f}
                    </li>
                  ))}
                </ul>
                <Button 
                  onClick={() => handleCheckout(yearlyBilling ? 'power_yearly' : 'power_monthly')}
                  disabled={loading || user?.plan === 'power'}
                  variant="outline"
                  className="w-full border-neon-violet text-neon-violet hover:bg-neon-violet/10"
                  data-testid="upgrade-power-btn"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 
                   user?.plan === 'power' ? 'Current Plan' : 'Upgrade to Power'}
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Credits Tab */}
        <TabsContent value="credits">
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { id: 'credits_100', credits: 100, price: 9, popular: false },
              { id: 'credits_500', credits: 500, price: 39, popular: true },
              { id: 'credits_1000', credits: 1000, price: 69, popular: false },
            ].map((pack) => (
              <Card key={pack.id} className={`bg-void-paper ${pack.popular ? 'border-2 border-neon-amber' : 'border-white/10'}`}>
                {pack.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-neon-amber text-black text-xs font-bold rounded-full">
                    BEST VALUE
                  </div>
                )}
                <CardHeader>
                  <CardTitle className="text-white font-heading flex items-center gap-2">
                    <Gift className="w-5 h-5 text-neon-amber" />
                    {pack.credits} Credits
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <span className="font-heading font-extrabold text-3xl text-white">${pack.price}</span>
                    <span className="text-gray-500 text-sm ml-2">one-time</span>
                  </div>
                  <p className="text-gray-400 text-sm">
                    ${(pack.price / pack.credits * 100).toFixed(1)}¢ per credit
                  </p>
                  <Button 
                    onClick={() => handleCheckout(pack.id)}
                    disabled={loading}
                    className={`w-full ${pack.popular ? 'bg-neon-amber text-black hover:bg-neon-amber/90' : 'bg-white/10 text-white hover:bg-white/20'}`}
                    data-testid={`buy-credits-${pack.credits}`}
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Purchase'}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history">
          <Card className="bg-void-paper border-white/10">
            <CardHeader>
              <CardTitle className="text-white font-heading">Payment History</CardTitle>
            </CardHeader>
            <CardContent>
              {transactions.length > 0 ? (
                <div className="space-y-3">
                  {transactions.map((tx) => (
                    <div key={tx.id} className="flex items-center justify-between p-4 bg-void rounded-lg">
                      <div>
                        <p className="text-white font-medium">{tx.package_id.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</p>
                        <p className="text-gray-500 text-sm">{new Date(tx.created_at).toLocaleDateString()}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-white font-mono">${(tx.amount / 100).toFixed(2)}</p>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          tx.payment_status === 'paid' 
                            ? 'bg-green-500/20 text-green-400' 
                            : tx.payment_status === 'failed' || tx.status === 'expired'
                              ? 'bg-red-500/20 text-red-400'
                              : 'bg-yellow-500/20 text-yellow-400'
                        }`}>
                          {tx.payment_status === 'paid' ? 'Completed' : 
                           tx.status === 'expired' ? 'Expired' : 
                           tx.payment_status === 'failed' ? 'Failed' : 'Pending'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <CreditCard className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-500">No payment history yet</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Billing;
